import React, { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { API_URL } from "../../constant";

export function LoginModal({ isOpen, onClose, onLoginSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = API_URL;
      const retailSecret = process.env.REACT_APP_RETAIL_SECRET || "-----BEGIN PRIVATE KEY----- Sk00Q1hqR3h4THJ4MEJGOFhQTW85MllSMjlCb1Uzb1Y= -----END PRIVATE KEY-----";

      const res = await fetch(`${apiUrl}/api/retail-business/customer/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Retail-Secret": retailSecret
        },
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Invalid credentials");
      }

      // Extract first_name based on user's API hint
      const name = data.first_name || (data.user && data.user.first_name) || data.name || (data.customer && data.customer.name) || email.split("@")[0];
      
      onLoginSuccess({
        name,
        email,
        ...data
      });
      
      setEmail("");
      setPassword("");
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.6)', padding: '1rem' }}>
      <div className="relative w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl" style={{ position: 'relative', width: '100%', maxWidth: '28rem', borderRadius: '1rem', backgroundColor: '#fff', padding: '2rem', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
          style={{ position: 'absolute', right: '1rem', top: '1rem', color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <X className="h-5 w-5" style={{ height: '1.25rem', width: '1.25rem' }} />
        </button>
        
        <div className="mb-6 text-center" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
          <h2 className="text-2xl font-black text-[#03173d]" style={{ fontSize: '1.5rem', fontWeight: 900, color: '#03173d', margin: 0 }}>Welcome Back</h2>
          <p className="mt-2 text-sm text-gray-500" style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>Log in to your TopCoin UK account</p>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-600 border border-red-200" style={{ marginBottom: '1rem', borderRadius: '0.375rem', backgroundColor: '#fef2f2', padding: '0.75rem', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #fecaca' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-700" htmlFor="email" style={{ marginBottom: '0.375rem', display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
              Email Address
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-900 focus:ring-1 focus:ring-blue-900"
              placeholder="you@example.com"
              style={{ width: '100%', borderRadius: '0.375rem', border: '1px solid #d1d5db', padding: '0.5rem 0.75rem', fontSize: '0.875rem', outline: 'none' }}
            />
          </div>
          
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-700" htmlFor="password" style={{ marginBottom: '0.375rem', display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-900 focus:ring-1 focus:ring-blue-900"
              placeholder="••••••••"
              style={{ width: '100%', borderRadius: '0.375rem', border: '1px solid #d1d5db', padding: '0.5rem 0.75rem', fontSize: '0.875rem', outline: 'none' }}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#03173d] hover:bg-[#072255] text-white py-2.5 rounded-md font-bold mt-2"
            style={{ width: '100%', backgroundColor: '#03173d', color: '#fff', padding: '0.625rem 0', borderRadius: '0.375rem', fontWeight: 700, marginTop: '0.5rem', border: 'none', cursor: isLoading ? 'not-allowed' : 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            {isLoading ? <Loader2 className="h-5 w-5 animate-spin" style={{ height: '1.25rem', width: '1.25rem' }} /> : "Login"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm" style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.875rem' }}>
          <span className="text-gray-500" style={{ color: '#6b7280' }}>Don't have an account? </span>
          <a
            href="https://staging.topcoinuk.com/sign_in"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold text-amber-500 hover:text-amber-600 hover:underline"
            style={{ fontWeight: 700, color: '#f59e0b', textDecoration: 'none' }}
          >
            Register Customer
          </a>
        </div>
      </div>
    </div>
  );
}
